import { useState } from "react";
import { useHelpRequests, useUpdateHelpRequest, useDeleteHelpRequest, HelpRequest } from "@/hooks/useHelpRequests";
import { useAuth } from "@/lib/auth";
import { useHasRole } from "@/hooks/useUserRoles";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { Card, CardContent } from "@/components/ui/card";
import { CreateHelpRequestDialog } from "./CreateHelpRequestDialog";
import { Hand, Clock, CheckCircle, MessageCircle, Trash2, Lock, Eye } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface HelpRequestsSectionProps {
  courseId: string;
  moduleId?: string | null;
  moduleName?: string;
}

export function HelpRequestsSection({ courseId, moduleId, moduleName }: HelpRequestsSectionProps) {
  const { user } = useAuth();
  const isAdmin = useHasRole("admin");
  const isInstructor = useHasRole("instructor");
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: requests, isLoading } = useHelpRequests(courseId, moduleId);
  const updateRequest = useUpdateHelpRequest();
  const deleteRequest = useDeleteHelpRequest();

  const handleResolve = async (request: HelpRequest) => {
    if (!user) return;
    
    try {
      await updateRequest.mutateAsync({
        id: request.id,
        course_id: courseId,
        status: "resolved",
        resolved_by: user.id,
      });
      toast.success("Help request marked as resolved");
    } catch {
      toast.error("Failed to update request");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await deleteRequest.mutateAsync({ id: deleteId, course_id: courseId });
      toast.success("Help request deleted");
      setDeleteId(null);
    } catch {
      toast.error("Failed to delete request");
    }
  };

  const getStatusBadge = (status: HelpRequest["status"]) => {
    switch (status) {
      case "pending":
        return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" /> Pending</Badge>;
      case "in_progress":
        return <Badge variant="default" className="gap-1"><MessageCircle className="h-3 w-3" /> In Progress</Badge>;
      case "resolved":
        return <Badge variant="outline" className="gap-1 text-green-600 border-green-600"><CheckCircle className="h-3 w-3" /> Resolved</Badge>;
    }
  };

  const canManageRequest = (request: HelpRequest) => {
    return request.user_id === user?.id || isAdmin || isInstructor;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Hand className="h-5 w-5" />
          Help Requests
        </h3>
        <Button onClick={() => setDialogOpen(true)} size="sm">
          <Hand className="h-4 w-4 mr-2" />
          Raise Hand
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading requests...</div>
      ) : requests?.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <Hand className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No help requests yet.</p>
            <p className="text-sm mt-1">Need help? Raise your hand to get assistance from mentors and instructors.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {requests?.map((request) => (
            <Card key={request.id} className={request.status === "resolved" ? "opacity-70" : ""}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <UserAvatar avatarPath={request.author?.avatar_url} fullName={request.author?.full_name} className="h-8 w-8 flex-shrink-0" thumbnailSize={64} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{request.author?.full_name || "Unknown"}</span>
                        {getStatusBadge(request.status)}
                        {request.is_private && (
                          <Badge variant="outline" className="gap-1 text-xs">
                            <Lock className="h-3 w-3" /> Private
                          </Badge>
                        )}
                        {!request.is_private && (
                          <Badge variant="outline" className="gap-1 text-xs text-muted-foreground">
                            <Eye className="h-3 w-3" /> Public
                          </Badge>
                        )}
                      </div>
                      <h4 className="font-medium mt-1">{request.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                        {request.content}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span>{formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}</span>
                        {request.resolver && (
                          <span>Resolved by {request.resolver.full_name}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {canManageRequest(request) && request.status !== "resolved" && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResolve(request)}
                        disabled={updateRequest.isPending}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Resolve
                      </Button>
                      {request.user_id === user?.id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeleteId(request.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateHelpRequestDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        courseId={courseId}
        moduleId={moduleId}
        moduleName={moduleName}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Help Request</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this help request? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
